import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FloatingOrbs } from './FloatingOrbs';
export const Services: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['10%', '-10%']);
  const titleY = useTransform(scrollYProgress, [0, 0.3], [50, 0]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  // Subtle sunrise glow continuation
  const sunriseOpacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.15, 0.1, 0.05, 0]);
  const pillars = [{
    title: 'System roadmap & architecture',
    description: 'We design Salesforce as the operational foundation of your business — not just a CRM, but the system that powers growth, automation, and intelligence. Every integration, workflow, and capability is planned as part of a long-term system architecture.',
    color: 'rgba(21, 200, 255, 0.12)',
    borderColor: 'rgba(21, 200, 255, 0.4)',
    accentColor: '#faff31'
  }, {
    title: 'Full-stack Salesforce ownership',
    description: 'From core clouds and industry capabilities to integrations, analytics, and AI, we design, build, and evolve your entire Salesforce system. No fragmented vendors. No disconnected layers. Just one cohesive platform.',
    color: 'rgba(21, 200, 255, 0.12)',
    borderColor: 'rgba(21, 200, 255, 0.4)',
    accentColor: '#faff31'
  }, {
    title: 'Intelligence built into the system',
    description: 'AI is designed into your Salesforce architecture from the start — embedded into workflows, decision-making, and operations. Not experimental pilots. Production-grade intelligence integrated into your core system.',
    color: 'rgba(21, 200, 255, 0.12)',
    borderColor: 'rgba(21, 200, 255, 0.4)',
    accentColor: '#faff31'
  }, {
    title: 'Continuous system evolution',
    description: 'Your system improves continuously — without needing to be rebuilt.',
    color: 'rgba(21, 200, 255, 0.12)',
    borderColor: 'rgba(21, 200, 255, 0.4)',
    accentColor: '#faff31'
  }];
  return <section id="services" ref={sectionRef} className="relative py-24 md:py-32 overflow-hidden" data-id="element-340">
      {/* Floating Energy Orbs */}
      <FloatingOrbs count={12} data-id="element-341" />

      {/* Continuing sunrise glow */}
      <motion.div className="absolute top-0 left-0 right-0 h-[50vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(250, 255, 49, 0.08) 0%, transparent 70%)',
      opacity: sunriseOpacity
    }} data-id="element-342" />

      {/* Bottom glow */}
      <motion.div className="absolute bottom-0 left-0 right-0 h-[60vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 100% 60% at 50% 100%, rgba(250, 255, 49, 0.15) 0%, transparent 60%)',
      opacity: sunriseOpacity
    }} data-id="element-343" />

      {/* Ambient yellow throughout section */}
      <div className="absolute inset-0 pointer-events-none opacity-25" data-id="element-344">
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-electric-yellow/8 rounded-full blur-3xl" data-id="element-345" />
        <div className="absolute bottom-1/4 left-1/3 w-[400px] h-[400px] bg-electric-yellow/6 rounded-full blur-3xl" data-id="element-346" />
      </div>

      <div className="container mx-auto px-4 md:px-6" data-id="element-347">
        <motion.div className="text-center mb-16" style={{
        y: titleY,
        opacity: titleOpacity
      }} data-id="element-348">
          <motion.h2 className="text-3xl md:text-4xl font-heading font-bold tracking-wider mb-4 text-white" initial={{
          scale: 0.8,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} data-id="element-349">
            From Roadmap to{' '}
            <span className="text-electric-yellow" data-id="element-350">Intelligent System</span>
          </motion.h2>
          <motion.p className="text-white/80 max-w-2xl mx-auto" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} viewport={{
          once: true
        }} data-id="element-351">
            We don't implement Salesforce. We design{' '}
            <span className="text-electric-yellow font-medium" data-id="element-352">
              the system your business runs on
            </span>
            .
          </motion.p>
        </motion.div>

        <div className="relative max-w-7xl mx-auto" data-id="element-353">
          {/* Connecting flow line between pillars */}
          <motion.div className="hidden lg:block absolute top-0 left-0 right-0 h-full pointer-events-none" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 1.2,
          delay: 0.5
        }} viewport={{
          once: true
        }} data-id="element-354">
            <svg className="absolute inset-0 w-full h-full" style={{
            overflow: 'visible'
          }} data-id="element-355">
              <motion.path d="M 12.5% 50 Q 25% 30, 37.5% 50 T 62.5% 50 T 87.5% 50" stroke="rgba(79, 12, 154, 0.3)" strokeWidth="2" fill="none" strokeDasharray="8 8" initial={{
              pathLength: 0,
              opacity: 0
            }} whileInView={{
              pathLength: 1,
              opacity: 0.5
            }} transition={{
              duration: 2,
              delay: 0.8,
              ease: 'easeInOut'
            }} viewport={{
              once: true
            }} data-id="element-356" />
            </svg>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" style={{
          y
        }} data-id="element-357">
            {pillars.map((pillar, index) => <motion.div key={index} className="relative group" initial={{
            opacity: 0,
            y: 60
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: index * 0.1,
            ease: 'easeOut'
          }} viewport={{
            once: true,
            amount: 0.2
          }} data-id="element-358">
                {/* Connecting line to next pillar (mobile/tablet) */}
                {index < pillars.length - 1 && <motion.div className="lg:hidden absolute -bottom-3 left-1/2 -translate-x-1/2 w-[2px] h-6 z-10" style={{
              background: `linear-gradient(to bottom, ${pillar.borderColor}, transparent)`
            }} initial={{
              scaleY: 0,
              opacity: 0
            }} whileInView={{
              scaleY: 1,
              opacity: 0.6
            }} transition={{
              duration: 0.4,
              delay: index * 0.1 + 0.6
            }} viewport={{
              once: true
            }} data-id="element-359" />}

                {/* Pillar structure */}
                <div className="relative h-full min-h-[400px] rounded-lg border backdrop-blur-sm p-6 flex flex-col" style={{
              backgroundColor: pillar.color,
              borderColor: pillar.borderColor,
              borderLeftWidth: '3px'
            }} data-id="element-360">
                  {/* Top accent line */}
                  <motion.div className="absolute top-0 left-0 right-0 h-[2px]" style={{
                background: `linear-gradient(to right, ${pillar.accentColor}, transparent)`
              }} initial={{
                scaleX: 0
              }} whileInView={{
                scaleX: 1
              }} transition={{
                duration: 0.8,
                delay: index * 0.1 + 0.3
              }} viewport={{
                once: true
              }} data-id="element-361" />

                  {/* Pillar indicator */}
                  <motion.div className="flex items-center gap-2 mb-6" initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: index * 0.1 + 0.4
              }} viewport={{
                once: true
              }} data-id="element-362">
                    <div className="w-1.5 h-8 rounded-full" style={{
                  backgroundColor: pillar.accentColor,
                  boxShadow: `0 0 10px ${pillar.accentColor}`
                }} data-id="element-363" />
                    <div className="text-xs font-semibold tracking-wider uppercase" style={{
                  color: pillar.accentColor
                }} data-id="element-364">
                      {String(index + 1).padStart(2, '0')}
                    </div>
                  </motion.div>

                  {/* Title */}
                  <motion.h3 className="text-xl md:text-2xl font-heading font-semibold mb-4 text-white leading-tight" initial={{
                opacity: 0
              }} whileInView={{
                opacity: 1
              }} transition={{
                duration: 0.6,
                delay: index * 0.1 + 0.5
              }} viewport={{
                once: true
              }} data-id="element-365">
                    {pillar.title}
                  </motion.h3>

                  {/* Description */}
                  <motion.p className="text-white/70 text-sm leading-relaxed flex-grow" initial={{
                opacity: 0
              }} whileInView={{
                opacity: 1
              }} transition={{
                duration: 0.6,
                delay: index * 0.1 + 0.6
              }} viewport={{
                once: true
              }} data-id="element-366">
                    {pillar.description}
                  </motion.p>

                  {/* Subtle grid overlay */}
                  <div className="absolute inset-0 opacity-[0.02] pointer-events-none rounded-lg" style={{
                backgroundImage: `
                        linear-gradient(90deg, ${pillar.borderColor} 1px, transparent 1px),
                        linear-gradient(0deg, ${pillar.borderColor} 1px, transparent 1px)
                      `,
                backgroundSize: '20px 20px'
              }} data-id="element-367" />
                </div>

                {/* Subtle glow on hover */}
                <motion.div className="absolute inset-0 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
              background: `radial-gradient(circle at center, ${pillar.accentColor}15 0%, transparent 70%)`
            }} data-id="element-368" />
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
};